import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import {
  getFirebaseClients,
  isFunctionsEmulatorOrLiveBackendConfigured,
  isLocalEmulatorMode,
} from './firebase.js';

export const GA4_OAUTH_SCOPE = 'https://www.googleapis.com/auth/analytics.edit';

export type GoogleAnalyticsStatusCode =
  'NOT_CONFIGURED' | 'DISABLED' | 'CONNECTED' | 'VERIFIED' | 'FAILED';

export interface GoogleAnalyticsDataStreamOption {
  readonly streamId: string;
  readonly streamName: string;
  readonly measurementId: string;
  readonly apiSecret?: string;
}

export interface GoogleAnalyticsPropertyOption {
  readonly propertyId: string;
  readonly propertyName: string;
  readonly streams: readonly GoogleAnalyticsDataStreamOption[];
}

export interface GoogleAnalyticsAccountOption {
  readonly accountId: string;
  readonly accountName: string;
  readonly properties: readonly GoogleAnalyticsPropertyOption[];
}

export const DEFAULT_GOOGLE_ANALYTICS_ACCOUNTS: readonly GoogleAnalyticsAccountOption[] = [
  {
    accountId: 'acc_acme_global',
    accountName: 'Acme Retail Group',
    properties: [
      {
        propertyId: 'prop_uk_web',
        propertyName: 'Acme UK Packaging & QR',
        streams: [
          {
            streamId: 'str_uk_qr',
            streamName: 'UK Product QR Stream',
            measurementId: 'G-1A2B3C4D5E',
            apiSecret: 'oauth_ga4_mp_secret_uk_qr',
          },
          {
            streamId: 'str_uk_campaign',
            streamName: 'UK Seasonal Campaign Stream',
            measurementId: 'G-UKCAMP2026',
            apiSecret: 'oauth_ga4_mp_secret_uk_camp',
          },
        ],
      },
      {
        propertyId: 'prop_eu_web',
        propertyName: 'Acme EU Digital Link',
        streams: [
          {
            streamId: 'str_eu_qr',
            streamName: 'EU Product QR Stream',
            measurementId: 'G-EUQR987654',
            apiSecret: 'oauth_ga4_mp_secret_eu_qr',
          },
        ],
      },
    ],
  },
  {
    accountId: 'acc_acme_labs',
    accountName: 'Acme Innovation Sandbox',
    properties: [
      {
        propertyId: 'prop_sandbox',
        propertyName: 'Sandbox QR Resolver',
        streams: [
          {
            streamId: 'str_sandbox_web',
            streamName: 'Sandbox Web Stream',
            measurementId: 'G-SANDBOX123',
            apiSecret: 'oauth_ga4_mp_secret_sandbox',
          },
        ],
      },
    ],
  },
];

export interface GoogleAnalyticsIntegrationState {
  readonly enabled: boolean;
  readonly measurementId: string;
  readonly hasApiSecret: boolean;
  readonly oauthConnected?: boolean;
  readonly status: GoogleAnalyticsStatusCode;
  readonly accountId?: string | null;
  readonly accountName?: string | null;
  readonly propertyId?: string | null;
  readonly propertyName?: string | null;
  readonly streamId?: string | null;
  readonly streamName?: string | null;
  readonly lastTestedAt?: string | null;
  readonly failureReason?: string | null;
}

export type SaveGoogleAnalyticsIntegrationResult = GoogleAnalyticsIntegrationState;

export interface SaveGoogleAnalyticsIntegrationPayload {
  readonly orgId: string;
  readonly workspaceId: string;
  readonly enabled: boolean;
  readonly measurementId: string;
  readonly apiSecret?: string;
  readonly oauthConnected?: boolean;
  readonly accountId?: string | null;
  readonly accountName?: string | null;
  readonly propertyId?: string | null;
  readonly propertyName?: string | null;
  readonly streamId?: string | null;
  readonly streamName?: string | null;
}

export interface ListGoogleAnalyticsAccountsPayload {
  readonly orgId: string;
  readonly workspaceId: string;
  readonly authorizationCode?: string;
  readonly refreshToken?: string;
  readonly accessToken?: string;
  readonly redirectUri?: string;
}

export interface ListGoogleAnalyticsAccountsResult {
  readonly oauthConnected: boolean;
  readonly scope: string;
  readonly accounts: readonly GoogleAnalyticsAccountOption[];
}

export interface ConnectGoogleAnalyticsStreamPayload {
  readonly orgId: string;
  readonly workspaceId: string;
  readonly enabled: boolean;
  readonly accountId: string;
  readonly accountName: string;
  readonly propertyId: string;
  readonly propertyName: string;
  readonly streamId: string;
  readonly streamName: string;
  readonly measurementId: string;
}

export type ConnectGoogleAnalyticsStreamResult = SaveGoogleAnalyticsIntegrationResult;

export interface DisconnectGoogleAnalyticsIntegrationPayload {
  readonly orgId: string;
  readonly workspaceId: string;
}

export interface DisconnectGoogleAnalyticsIntegrationResult {
  readonly enabled: false;
  readonly measurementId: '';
  readonly hasApiSecret: false;
  readonly oauthConnected: false;
  readonly status: GoogleAnalyticsStatusCode;
  readonly tokenRevoked: boolean;
  readonly disconnectedAt: string;
}

function invokeCallable<Req, Res>(name: string, payload: Req): Promise<Res> {
  const { functions } = getFirebaseClients();
  return httpsCallable<Req, Res>(functions, name)(payload).then((r) => r.data);
}

export async function callListGoogleAnalyticsAccountsFunction(
  payload: ListGoogleAnalyticsAccountsPayload,
): Promise<ListGoogleAnalyticsAccountsResult> {
  if (
    !import.meta.env.PROD &&
    isLocalEmulatorMode() &&
    !isFunctionsEmulatorOrLiveBackendConfigured()
  ) {
    return {
      oauthConnected: true,
      scope: GA4_OAUTH_SCOPE,
      accounts: DEFAULT_GOOGLE_ANALYTICS_ACCOUNTS,
    };
  }
  return invokeCallable('listGoogleAnalyticsAccounts', payload);
}

export function callConnectGoogleAnalyticsStreamFunction(
  payload: ConnectGoogleAnalyticsStreamPayload,
): Promise<ConnectGoogleAnalyticsStreamResult> {
  return invokeCallable('connectGoogleAnalyticsStream', payload);
}

export function callDisconnectGoogleAnalyticsIntegrationFunction(
  payload: DisconnectGoogleAnalyticsIntegrationPayload,
): Promise<DisconnectGoogleAnalyticsIntegrationResult> {
  return invokeCallable('disconnectGoogleAnalyticsIntegration', payload);
}

export async function initiateGoogleAnalyticsOAuthAndListAccounts(params: {
  readonly orgId: string;
  readonly workspaceId: string;
}): Promise<readonly GoogleAnalyticsAccountOption[]> {
  let accessToken: string | undefined;
  if (import.meta.env.PROD || !isLocalEmulatorMode()) {
    const { auth } = getFirebaseClients();
    const provider = new GoogleAuthProvider();
    provider.addScope(GA4_OAUTH_SCOPE);
    provider.setCustomParameters({ access_type: 'offline', prompt: 'consent' });
    const cred = await signInWithPopup(auth, provider);
    accessToken = GoogleAuthProvider.credentialFromResult(cred)?.accessToken ?? undefined;
  }
  const res = await callListGoogleAnalyticsAccountsFunction({
    orgId: params.orgId,
    workspaceId: params.workspaceId,
    ...(accessToken ? { accessToken } : {}),
  });
  return res.accounts;
}

export function callSaveGoogleAnalyticsIntegrationFunction(
  payload: SaveGoogleAnalyticsIntegrationPayload,
): Promise<SaveGoogleAnalyticsIntegrationResult> {
  return invokeCallable('saveGoogleAnalyticsIntegration', payload);
}

export interface SendGoogleAnalyticsTestEventPayload {
  readonly orgId: string;
  readonly workspaceId: string;
  readonly measurementId: string;
}

export interface SendGoogleAnalyticsTestEventResult {
  readonly ok: boolean;
  readonly status: 'VERIFIED' | 'FAILED';
  readonly testedAt: string;
  readonly failureReason?: string | null;
}

export function callSendGoogleAnalyticsTestEventFunction(
  payload: SendGoogleAnalyticsTestEventPayload,
): Promise<SendGoogleAnalyticsTestEventResult> {
  return invokeCallable('sendGoogleAnalyticsTestEvent', payload);
}

export function normalizeGoogleAnalyticsState(
  raw?: Partial<GoogleAnalyticsIntegrationState>,
): GoogleAnalyticsIntegrationState {
  const measurementId = (raw?.measurementId ?? '').trim();
  const hasApiSecret = Boolean(raw?.hasApiSecret);
  const enabled = Boolean(raw?.enabled);
  const defaultStatus: GoogleAnalyticsStatusCode =
    measurementId.length === 0 && !hasApiSecret
      ? 'NOT_CONFIGURED'
      : enabled
        ? 'CONNECTED'
        : 'DISABLED';

  return {
    enabled,
    measurementId,
    hasApiSecret,
    oauthConnected: Boolean(raw?.oauthConnected),
    status: raw?.status ?? defaultStatus,
    accountId: raw?.accountId ?? null,
    accountName: raw?.accountName ?? null,
    propertyId: raw?.propertyId ?? null,
    propertyName: raw?.propertyName ?? null,
    streamId: raw?.streamId ?? null,
    streamName: raw?.streamName ?? null,
    lastTestedAt: raw?.lastTestedAt ?? null,
    failureReason: raw?.failureReason ?? null,
  };
}

export function formatGoogleAnalyticsPlainStatus(state: GoogleAnalyticsIntegrationState): string {
  if (state.status === 'FAILED') {
    const reason = state.failureReason?.trim();
    return reason && reason.length > 0 ? `Failed — ${reason}` : 'Failed — test event rejected';
  }
  if (!state.measurementId && !state.hasApiSecret) return 'Not set up';
  if (!state.enabled || state.status === 'DISABLED') return 'Off';
  if (state.status === 'VERIFIED') return 'Connected (test event sent)';
  return 'Connected';
}

export function formatGoogleAnalyticsBadgeLabel(
  state: GoogleAnalyticsIntegrationState,
): 'Not set up' | 'Off' | 'Connected' | 'Failed' {
  if (state.status === 'FAILED') return 'Failed';
  if (!state.measurementId && !state.hasApiSecret) return 'Not set up';
  if (!state.enabled || state.status === 'DISABLED') return 'Off';
  return 'Connected';
}

export function formatGoogleAnalyticsBadgeVariant(
  state: GoogleAnalyticsIntegrationState,
): 'success' | 'destructive' | 'secondary' | 'outline' {
  if (state.status === 'FAILED') return 'destructive';
  if (!state.measurementId && !state.hasApiSecret) return 'outline';
  if (!state.enabled || state.status === 'DISABLED') return 'secondary';
  return 'success';
}

export function validateGoogleAnalyticsDraft(
  measurementId: string,
  apiSecretInput: string,
  hasExistingSecret: boolean,
  allowOAuthAutoSecret = false,
):
  | {
      readonly ok: true;
      readonly measurementId: string;
      readonly apiSecret: string | undefined;
    }
  | { readonly ok: false; readonly error: string } {
  const cleanedId = measurementId.trim().toUpperCase();
  if (!/^G-[A-Z0-9]{4,20}$/.test(cleanedId)) {
    return {
      ok: false,
      error:
        'Please enter a valid Google Analytics 4 Measurement ID starting with G- (for example, G-1A2B3C4D5E).',
    };
  }
  const trimmedSecret = apiSecretInput.trim();
  if (!hasExistingSecret && !allowOAuthAutoSecret && trimmedSecret.length === 0) {
    return {
      ok: false,
      error: 'Please enter your Google Analytics Measurement Protocol API secret.',
    };
  }
  return {
    ok: true,
    measurementId: cleanedId,
    apiSecret: trimmedSecret.length > 0 ? trimmedSecret : undefined,
  };
}

export function findConnectedStreamMetadata(
  accounts: readonly GoogleAnalyticsAccountOption[],
  measurementId: string,
) {
  for (const acc of accounts) {
    for (const prop of acc.properties) {
      for (const stream of prop.streams) {
        if (stream.measurementId.toUpperCase() === measurementId.toUpperCase()) {
          return {
            accountId: acc.accountId,
            accountName: acc.accountName,
            propertyId: prop.propertyId,
            propertyName: prop.propertyName,
            streamId: stream.streamId,
            streamName: stream.streamName,
          };
        }
      }
    }
  }
  return undefined;
}

export function mergeSavedGoogleAnalyticsState(
  prev: GoogleAnalyticsIntegrationState,
  result: SaveGoogleAnalyticsIntegrationResult,
  matchedMeta?: ReturnType<typeof findConnectedStreamMetadata>,
): GoogleAnalyticsIntegrationState {
  return {
    enabled: result.enabled,
    measurementId: result.measurementId,
    hasApiSecret: result.hasApiSecret,
    oauthConnected: Boolean(result.oauthConnected ?? prev.oauthConnected),
    status: result.status,
    accountId: result.accountId ?? matchedMeta?.accountId ?? prev.accountId ?? null,
    accountName: result.accountName ?? matchedMeta?.accountName ?? prev.accountName ?? null,
    propertyId: result.propertyId ?? matchedMeta?.propertyId ?? prev.propertyId ?? null,
    propertyName: result.propertyName ?? matchedMeta?.propertyName ?? prev.propertyName ?? null,
    streamId: result.streamId ?? matchedMeta?.streamId ?? prev.streamId ?? null,
    streamName: result.streamName ?? matchedMeta?.streamName ?? prev.streamName ?? null,
    lastTestedAt: result.lastTestedAt ?? prev.lastTestedAt ?? null,
    failureReason: result.failureReason ?? null,
  };
}

export interface GoogleAnalyticsConfigModalProps {
  readonly open: boolean;
  readonly workspaceId: string;
  readonly hasExistingSecret: boolean;
  readonly draftEnabled: boolean;
  readonly setDraftEnabled: (v: boolean) => void;
  readonly draftMeasurementId: string;
  readonly setDraftMeasurementId: (v: string) => void;
  readonly draftApiSecret: string;
  readonly setDraftApiSecret: (v: string) => void;
  readonly accounts?: readonly GoogleAnalyticsAccountOption[];
  readonly googleConnected?: boolean;
  readonly onConnectGoogleAccount?: () => void;
  readonly selectedAccountId?: string;
  readonly onSelectAccount?: (accountId: string) => void;
  readonly selectedPropertyId?: string;
  readonly onSelectProperty?: (propertyId: string) => void;
  readonly selectedStreamId?: string;
  readonly onSelectStream?: (streamId: string) => void;
  readonly submitting: boolean;
  readonly errorMessage: string | null;
  readonly onClose: () => void;
  readonly onSubmit: (e: { preventDefault: () => void }) => void;
}

export function resolveSelectedStreamOption(
  list: readonly GoogleAnalyticsAccountOption[],
  accId: string,
  propId: string,
  strId: string,
) {
  const acc = list.find((a) => a.accountId === accId) ?? list[0];
  const prop = acc?.properties.find((p) => p.propertyId === propId) ?? acc?.properties[0];
  const stream = prop?.streams.find((s) => s.streamId === strId) ?? prop?.streams[0];
  return acc && prop && stream ? { acc, prop, stream } : null;
}
